import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as React from "react";
import {
  Stack,
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  Modal,
  Paper,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Sidebar from "../Dashboard/Sidebar.js";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";

// --- MODERN STYLED COMPONENTS (Matched with CallLogs.js & Leads.js) ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const StyledDataGrid = styled(DataGrid)({
  border: "none",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontWeight: 700,
    fontSize: "14px",
    borderBottom: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-toolbarContainer": {
    padding: "16px",
    gap: "12px",
    borderBottom: "1px solid #E2E8F0",
  },
});

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "500px",
  maxHeight: "90vh",
  overflowY: "auto",
  backgroundColor: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
  outline: "none",
  padding: "24px",
  [theme.breakpoints.down("sm")]: {
    width: "95%",
  },
}));

function ClosingReasons(props) {
  const navigate = useNavigate();

  return (
    <RootContainer>
      <Sidebar />
      <ReasonsTable />
    </RootContainer>
  );
}

const ReasonsTable = () => {
  const [open, setOpen] = React.useState(false);
  const [reasons, setReasons] = React.useState([]);
  const [isLoading, setisLoading] = React.useState(false);
  const [newReason, setNewReason] = React.useState({});

  const handleOpen = () => {
    setNewReason({});
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  function handleChange(evt) {
    const value = evt.target.value;
    setNewReason({
      ...newReason,
      [evt.target.name]: value,
    });
  }

  async function handleDelete(Event, Value) {
    if (window.confirm("You surely want to delete this Reason?")) {
      await deleteUser(Value.row.uuid);
    } else {
      await getAllReasons();
    }
  }

  const getAllReasons = async () => {
    setisLoading(true);
    const token = localStorage.getItem("accessToken");
    const config = {
      headers: {
        auth: token,
      },
    };
    axios.get("/closingReasons", config).then((response) => {
      setReasons(response.data.data);
      setisLoading(false);
    });
  };

  React.useEffect(() => {
    getAllReasons();
  }, []);

  const addNewReason = async () => {
    setisLoading(true);
    const token = localStorage.getItem("accessToken");
    const config = {
      headers: {
        auth: token,
      },
    };
    await axios
      .post("/addLeadClosingReason", newReason, config)
      .then((response) => {
        toast.success("Reason Added Successfully!");
        setOpen(false);
        setisLoading(false);
        getAllReasons();
      })
      .catch((err) => {
        console.log(err);
        if (err.response.data.error == "Reason Already Exists") {
          toast.error("Reason Already Exists");
          setisLoading(false);
        } else {
          toast.error("Reason Cannot Be Added");
          setisLoading(false);
        }
      });
  };

  const deleteUser = async (uuid) => {
    const token = localStorage.getItem("accessToken");
    const config = {
      headers: {
        auth: token,
      },
    };
    await axios
      .delete(`/deleteLeadClosingReason/${uuid}`, config)
      .then((response) => {
        toast.success("Reason Deleted Successfully!");
        setOpen(false);
        getAllReasons();
      })
      .catch((err) => {
        console.log(err);
        if (err.response.data.error == "User Already Exists") {
          toast.error("Reason Already Exists");
        } else {
          toast.error("Reason Cannot Be Deleted");
        }
      });
  };

  const columns = [
    {
      field: "col0",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (cellValues) => {
        return (
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
            onClick={(event) => {
              handleDelete(event, cellValues);
            }}
          >
            Delete
          </Button>
        );
      },
    },
    {
      field: "uuid",
      sortable: false,
      headerName: "UUID",
      width: 300,
      flex: 0.5,
    },
    {
      field: "reason",
      sortable: false,
      headerName: "Closing Reason",
      width: 300,
      flex: 1,
      renderCell: (params) => (
        <Typography sx={{ fontWeight: 500, color: "#1E293B" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      sortable: false,
      headerName: "Created At",
      width: 200,
      valueFormatter: (params) => {
        const valueFormatted = new Date(params.value).toDateString();
        return `${valueFormatted}`;
      },
    },
  ];

  return (
    <ContentArea>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: "#1E293B",
              fontFamily: "Kamerik",
              letterSpacing: "-1.5px",
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Manage Closing Reasons
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500, mt: 1 }}>
            Define reasons for closing leads to track outcomes effectively.
          </Typography>
        </Box>

        <Button
          variant="contained"
          sx={{
            height: "48px",
            borderRadius: "12px",
            fontWeight: 700,
            bgcolor: "#1E293B",
            "&:hover": { bgcolor: "#0F172A" },
            width: { xs: "100%", md: "auto" },
            px: 4,
          }}
          onClick={handleOpen}
        >
          Add Reason
        </Button>
      </Stack>

      <GlassCard sx={{ height: "75vh", overflow: "hidden" }}>
        <StyledDataGrid
          rows={reasons}
          columns={columns}
          pageSize={50}
          disableSelectionOnClick
          loading={isLoading}
          components={{
            Toolbar: GridToolbar,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          getRowId={(row) => row.uuid || Math.random()} 
        />
      </GlassCard>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <ModalBox>
          <Typography
            variant="h5"
            sx={{ color: "#1E293B", mb: 3, fontWeight: 800 }}
          >
            Add New Reason
          </Typography>

          <TextField
            id="filled-basic"
            label="Reason Description"
            variant="outlined"
            multiline
            rows={4}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
              },
            }}
            name="reason"
            onChange={handleChange}
            placeholder="Type the closing reason here..."
          />
          <Box mt={3}>
            <Button
              variant="contained"
              sx={{
                width: "100%",
                height: "48px",
                borderRadius: "12px",
                bgcolor: "#1E293B",
                fontWeight: 700,
                "&:hover": { bgcolor: "#0F172A" },
              }}
              onClick={addNewReason}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Add Reason"}
            </Button>
          </Box>
        </ModalBox>
      </Modal>

      <ToastContainer />
    </ContentArea>
  );
};
export default ClosingReasons;
